import React, {
  FunctionComponent,
  StrictMode,
  useEffect,
  useState
} from "react";
import {
  Avatar,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  RootRef,
  Snackbar,
  TextField
} from "@material-ui/core";
import useStyles from "../styles";
import { ImageEditor } from "../../../components/editors/ImageEditor";
import MaterialTable from "@material-table/core";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import * as tenantApi from "../../../api/tenantApi";
import * as translationApi from "../../../api/translationApi";
import { Alert } from "@material-ui/lab";
import { ConfirmDialog } from "../../../components/dialogs/ConfirmDialog";
import Typography from "@material-ui/core/Typography";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { DragHandle } from "@material-ui/icons";
import { Language, TranslationV2 } from "../../../models/Translation";
import { useSelector } from "react-redux";
import { AppState } from "../../../store";
import { asList, useLoadIfEmpty } from "../../../hooks/useLoadIfEmpty";
import { thunkFetchLanguages } from "../../../store/languages/actions";
import { getTranslationValuesOrDefault } from "../../../utils";
import { OnBoarding, Tenant } from "../../../models/Tenant";
import { useI18n } from "../../../hooks/useI18n";

export interface OnBoardingEditorProps {
  tenantId: string;
  tenants: ReadonlyArray<Tenant>;
  onSave: () => void;
}

export const OnBoardingEditor: FunctionComponent<OnBoardingEditorProps> = ({
  tenantId,
  tenants,
  onSave
}) => {
  const classes = useStyles();
  const { t } = useI18n();

  const languages = asList<Language>(
    useSelector<AppState, unknown>(s => s.languages)
  );

  useLoadIfEmpty(!languages.length, thunkFetchLanguages);

  const [state, setState] = useState<any>({
    loading: false,
    currentTenant: {} as Tenant,
    showEditOnBoarding: false,
    showDeleteOnBoarding: false,
    showSortOnBoarding: false,
    reOrderOnBoardings: [] as OnBoarding[],
    editingOnBoarding: {} as OnBoarding,
    image: {} as Blob,
    onBoardings: [] as OnBoarding[]
  });

  useEffect(() => {
    const populateOnBoardings = async () => {
      const tenant = tenants.find(x => x.id === tenantId);
      const onBoardings = tenant?.content?.onBoarding ?? ([] as OnBoarding[]);
      for (const onBoarding of onBoardings) {
        if (onBoarding.textId) {
          onBoarding.textTranslation = await getTranslation(onBoarding.textId);
        } else {
          onBoarding.textTranslation = {} as TranslationV2;
        }

        if (onBoarding.titleId) {
          onBoarding.titleTranslation = await getTranslation(
            onBoarding.titleId
          );
        } else {
          onBoarding.titleTranslation = {} as TranslationV2;
        }
      }

      setState({
        ...state,
        onBoardings: onBoardings ?? ([] as OnBoarding[]),
        currentTenant: tenant,
        loading: false
      });
    };

    if (!state.loading) {
      setState({ ...state, loading: true });
      populateOnBoardings();
    }
  }, [tenants]);

  const getTranslation = async (id: string) => {
    const result = await translationApi.getById(id);
    if (result.type === "OK") {
      return result.value as TranslationV2;
    }
    return {} as TranslationV2;
  };

  const updateOnBoardingList = (current: OnBoarding) => {
    let onBoardings =
      state.onBoardings && state.onBoardings
        ? [...state.onBoardings]
        : ([] as OnBoarding[]);

    const index = onBoardings.findIndex(x => x.id === current.id);

    if (index === -1) onBoardings.push(current);
    else onBoardings[index] = current;

    return onBoardings;
  };

  const deleteFromOnboardingList = (current: OnBoarding) => {
    let onBoardings =
      state.onBoardings && state.onBoardings
        ? [...state.onBoardings]
        : ([] as OnBoarding[]);

    const index = onBoardings.findIndex(x => x.id === current.id);

    if (index === -1) return onBoardings;
    else onBoardings.splice(index, 1);

    return onBoardings;
  };

  const setTranslationId = (type: string) => {
    const max = state.onBoardings?.length
      ? state.onBoardings.reduce((a: any, b: any) => (a.id > b.id ? a : b)).id
      : 0;

    return `tenant.${tenantId}.onBoarding.${max + 1}.${type}`;
  };

  const upsertTranslations = async (
    onBoarding: OnBoarding
  ): Promise<boolean> => {
    let translationResult = await translationApi.upsert({
      id: onBoarding.titleId,
      values: onBoarding.titleTranslation.values
    });

    if (translationResult.type !== "OK") {
      setState({ ...state, editOrUpdateOnBoardingFailed: true });
      return false;
    }

    translationResult = await translationApi.upsert({
      id: onBoarding.textId,
      values: onBoarding.textTranslation.values
    });

    if (translationResult.type !== "OK") {
      setState({ ...state, editOrUpdateOnBoardingFailed: true });
      return false;
    }

    return true;
  };

  const reorder = (list: any, startIndex: any, endIndex: any): any => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
  };

  const getItemStyle = (isDragging: any, draggableStyle: any) => ({
    ...draggableStyle,
    borderBottom: "1px solid rgb(235,235,235)",
    ...(isDragging && {
      background: "rgb(235,235,235)"
    })
  });

  const onDragEnd = (result: any) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const items = reorder(
      state.reOrderOnBoardings,
      result.source.index,
      result.destination.index
    );

    for (let i = 0; i < items.length; i++) {
      items[i].order = i + 1;
    }

    setState({
      ...state,
      reOrderOnBoardings: items as []
    });
  };

  const getTranslationValue = (code: string, type: string) => {
    if (type === "title") {
      return state.editingOnBoarding?.titleTranslation?.values[code]
        ? state.editingOnBoarding.titleTranslation.values[code]
        : "";
    }

    return state.editingOnBoarding?.textTranslation?.values[code]
      ? state.editingOnBoarding.textTranslation.values[code]
      : "";
  };

  return (
    <>
      <MaterialTable
        style={{ marginTop: "2rem" }}
        title={t("common.actions.onboarding")}
        isLoading={state.loading}
        columns={[
          { title: t("common.onboardingTable.order"), field: "order" },
          { title: t("common.onboardingTable.title"), field: "titleId" },
          { title: t("common.onboardingTable.text"), field: "textId" },
          { title: t("common.onboardingTable.image"), field: "image" }
        ]}
        data={
          state.onBoardings && state.onBoardings?.length > 0
            ? state.onBoardings.map((o: any) => ({
                id: o.id,
                order: o.order,
                titleId: o.titleTranslation.values
                  ? o.titleTranslation.values[
                      Object.keys(o.titleTranslation.values)[0]
                    ]
                  : o.titleId,
                textId: o.textTranslation.values
                  ? o.textTranslation.values[
                      Object.keys(o.textTranslation.values)[0]
                    ]
                  : o.textId,
                image: (
                  <Avatar
                    variant={"square"}
                    imgProps={{ style: { objectFit: "contain" } }}
                    src={o.imageUrl}
                    alt={t("common.onboardingTable.image")}
                  >
                    {t("common.onboardingTable.image")}
                  </Avatar>
                )
              }))
            : []
        }
        options={{
          actionsColumnIndex: -1,
          actionsCellStyle: { whiteSpace: "nowrap" },
          pageSize: 5,
          search: false
        }}
        localization={{
          header: {
            actions: ""
          }
        }}
        actions={[
          {
            icon: "sort_by_alpha",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.sortOnboardings"),
            isFreeAction: true,
            onClick: () => {
              setState({
                ...state,
                showSortOnBoarding: true,
                reOrderOnBoardings: [...state.onBoardings]
              });
            }
          },
          {
            icon: "add_circle",
            iconProps: {
              style: { color: "rgb(31, 132, 52)", fontSize: "2rem" }
            },
            tooltip: t("common.onboardingMessages.addNewOnboarding"),
            isFreeAction: true,
            onClick: () => {
              setState({
                ...state,
                showEditOnBoarding: true,
                editingOnBoarding: {
                  titleId: setTranslationId("title"),
                  textId: setTranslationId("text")
                } as OnBoarding
              });
            }
          },
          {
            icon: "edit",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.editOnboarding"),
            onClick: (event, rowData: any) => {
              const editingOnBoarding = state.onBoardings.find(
                (o: any) => o.id == rowData?.id
              );
              setState({
                ...state,
                showEditOnBoarding: true,
                editingOnBoarding: editingOnBoarding
              });
            }
          },
          {
            icon: "delete",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.deleteOnboarding"),
            onClick: (event, rowData: any) => {
              const editingOnBoarding = state.onBoardings.find(
                (o: any) => o.id == rowData?.id
              );
              setState({
                ...state,
                showDeleteOnBoarding: true,
                editingOnBoarding: editingOnBoarding
              });
            }
          }
        ]}
      />

      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={state.showEditOnBoarding}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <IconButton
            aria-label={t("common.actions.close")}
            className={classes.closeButton}
            onClick={() => setState({ ...state, showEditOnBoarding: false })}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          {state.currentTenant &&
            state.currentTenant.languageIds &&
            state.currentTenant.languageIds.map((x: any) => {
              let language = languages.find(y => y.id === x);
              if (!language) return;
              return (
                <React.Fragment key={language?.code}>
                  <TextField
                    className={classes.field}
                    label={`${t("common.onboardingForm.title")} ${
                      language?.name
                    }`}
                    required
                    value={getTranslationValue(language.code, "title")}
                    onChange={e => {
                      if (!language) return;
                      let translationValues = getTranslationValuesOrDefault(
                        state.editingOnBoarding?.titleTranslation
                      );
                      translationValues[language.code] = e.target.value;
                      setState({
                        ...state,
                        editingOnBoarding: {
                          ...state.editingOnBoarding,
                          titleTranslation: {
                            ...state.editingOnBoarding.titleTranslation,
                            values: translationValues
                          }
                        }
                      });
                    }}
                  />
                  <TextField
                    className={classes.field}
                    label={`${t("common.onboardingForm.text")} ${
                      language?.name
                    }`}
                    required
                    value={getTranslationValue(language.code, "text")}
                    onChange={e => {
                      if (!language) return;
                      let translationValues = getTranslationValuesOrDefault(
                        state.editingOnBoarding?.textTranslation
                      );
                      translationValues[language.code] = e.target.value;
                      setState({
                        ...state,
                        editingOnBoarding: {
                          ...state.editingOnBoarding,
                          textTranslation: {
                            ...state.editingOnBoarding.textTranslation,
                            values: translationValues
                          }
                        }
                      });
                    }}
                  />
                </React.Fragment>
              );
            })}
          <ImageEditor
            title={t("common.onboardingForm.image")}
            acceptedFiles={["image/png"]}
            initial={{
              ...state,
              url: state.editingOnBoarding?.imageUrl ?? ""
            }}
            onChange={data =>
              setState({
                ...state,
                image: data
              })
            }
          />
          <div
            style={{ display: "flex", justifyContent: "center", marginTop: 30 }}
          >
            <Button
              style={{ width: 300, margin: "auto", display: "flex" }}
              variant="contained"
              color="primary"
              disabled={state.loading}
              onClick={async () => {
                setState({ ...state, loading: true });
                let onBoarding = { ...state.editingOnBoarding };
                if (state.image?.blob) {
                  const uploadResult = await tenantApi.uploadOnBoardingImage(
                    tenantId,
                    state.image.blob
                  );

                  if (uploadResult.type !== "OK") {
                    setState({ ...state, editOrUpdateOnBoardingFailed: true });
                    return;
                  } else {
                    onBoarding.imageId = uploadResult.value.imageId;
                    onBoarding.imageVersion = uploadResult.value.imageVersion;
                  }
                }

                if (!(await upsertTranslations(onBoarding))) {
                  setState({ ...state, editOrUpdateOnBoardingFailed: true });
                  return;
                }

                const onBoardings = updateOnBoardingList(onBoarding);

                const result = await tenantApi.upsertOnBoardings(
                  tenantId,
                  onBoardings
                );

                if (result.type !== "OK") {
                  setState({ ...state, editOrUpdateOnBoardingFailed: true });
                } else {
                  onSave();
                  setState({
                    ...state,
                    showEditOnBoarding: false,
                    upsertSuccess: true,
                    loading: false
                  });
                }
              }}
            >
              {t("common.onboardingForm.saveOnboarding")}{" "}
              {state.loading && (
                <CircularProgress size={20} style={{ marginLeft: 20 }} />
              )}
            </Button>
            {state.editOrUpdateOnBoardingFailed && (
              <Alert severity="error">
                {t("common.onboardingMessages.errorAddingUpdating")}
              </Alert>
            )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={state.showSortOnBoarding}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle disableTypography>
          <Typography variant="h6">
            {t("common.messages.onboardingOrder")}
          </Typography>
          <IconButton
            aria-label={t("common.actions.close")}
            className={classes.closeButton}
            onClick={() => {
              setState({
                ...state,
                showSortOnBoarding: false
              });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <div>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="droppable">
                {(provided: any, snapshot: any) => (
                  <RootRef rootRef={provided.innerRef}>
                    <List dense ref={provided.innerRef}>
                      {state.reOrderOnBoardings.map(
                        (item: OnBoarding, index: any) => (
                          <Draggable
                            key={item.id}
                            draggableId={item.id.toString()}
                            index={index}
                          >
                            {(provided: any, snapshot: any) => (
                              <ListItem
                                ref={provided.innerRef}
                                ContainerComponent="li"
                                ContainerProps={{ ref: provided.innerRef }}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                style={getItemStyle(
                                  snapshot.isDragging,
                                  provided.draggableProps.style
                                )}
                              >
                                <ListItemIcon>
                                  <DragHandle />
                                </ListItemIcon>
                                <ListItemText
                                  primary={
                                    item.titleTranslation.values
                                      ? item.titleTranslation.values[
                                          Object.keys(
                                            item.titleTranslation.values
                                          )[0]
                                        ]
                                      : item.titleId
                                  }
                                  secondary={
                                    item.textTranslation.values
                                      ? item.textTranslation.values[
                                          Object.keys(
                                            item.textTranslation.values
                                          )[0]
                                        ]
                                      : item.textId
                                  }
                                />
                                <ListItemAvatar>
                                  <Avatar src={item.imageUrl} />
                                </ListItemAvatar>
                              </ListItem>
                            )}
                          </Draggable>
                        )
                      )}
                      {provided.placeholder}
                    </List>
                  </RootRef>
                )}
              </Droppable>
            </DragDropContext>
          </div>
          <Button
            style={{ width: 300, margin: "auto", display: "flex" }}
            variant="contained"
            color="primary"
            disabled={state.loading}
            onClick={async () => {
              setState({ ...state, loading: true });

              const result = await tenantApi.upsertOnBoardings(
                tenantId,
                state.reOrderOnBoardings
              );

              if (result.type !== "OK") {
                setState({ ...state, editOrUpdateOnBoardingFailed: true });
              } else {
                onSave();
                setState({
                  ...state,
                  showSortOnBoarding: false,
                  upsertSuccess: true,
                  loading: false
                });
              }
            }}
          >
            {t("common.onboardingForm.saveOnboardingOrder")}{" "}
            {state.loading && (
              <CircularProgress size={20} style={{ marginLeft: 20 }} />
            )}
          </Button>
          {state.error?.title && (
            <Alert severity="error">
              {t("common.onboardingMessages.errorOrdering")}
            </Alert>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        visible={state.showDeleteOnBoarding}
        titleText={t("common.onboardingMessages.removeOnboarding")}
        bodyText={t("common.onboardingMessages.removeOnboardingConfirmation")}
        confirmText={t("common.onboardingMessages.removeOnboarding")}
        onConfirm={async () => {
          const onBoardings = deleteFromOnboardingList(state.editingOnBoarding);

          const result = await tenantApi.upsertOnBoardings(
            tenantId,
            onBoardings
          );

          if (result.type !== "OK") {
            setState({ ...state, deleteOnBoardingFailed: true });
          } else {
            setState({
              ...state,
              showDeleteOnBoarding: false,
              upsertSuccess: true
            });
            onSave();
          }
          return true;
        }}
        onCancel={() => {
          setState({ ...state, showDeleteOnBoarding: false });
        }}
      >
        {state.deleteOnBoardingFailed && (
          <Alert severity="error">
            {t("common.onboardingMessages.errorDeleting")}
          </Alert>
        )}
      </ConfirmDialog>

      <Snackbar
        open={state.upsertSuccess}
        autoHideDuration={3000}
        onClose={() => {
          setState({
            ...state,
            upsertSuccess: false
          });
        }}
      >
        <Alert severity={"success"}>
          {t("common.messages.successfullyUpdatedOnboardings")}
        </Alert>
      </Snackbar>
    </>
  );
};
